import {
  Alert, Box, Button, Card, CardContent, Chip, Grid, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import { api, extractError } from '../../../shared/api';
import type { Attendance, AttendanceStatus, Class, Student } from '../../../shared/types';
import { useAuthStore } from '../../../features/auth/store';
import PageHeader from '../../../shared/PageHeader';
import { todayISO } from '../../../shared/utils';

export default function AttendancePage() {
  const user = useAuthStore((s) => s.user);
  if (user?.role === 'STUDENT') return <StudentAttendanceView />;
  return <TeacherAttendanceMark />;
}

const STATUSES: { value: AttendanceStatus; label: string; color: string }[] = [
  { value: 'PRESENT', label: 'P', color: '#10B981' },
  { value: 'ABSENT', label: 'A', color: '#EF4444' },
  { value: 'LATE', label: 'L', color: '#F97316' },
  { value: 'EXCUSED', label: 'E', color: '#0EA5E9' },
];

function TeacherAttendanceMark() {
  const [classId, setClassId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [marks, setMarks] = useState<Record<string, AttendanceStatus>>({});
  const qc = useQueryClient();

  const classesQ = useQuery({ queryKey: ['classes'], queryFn: async () => (await api.get<Class[]>('/classes')).data });
  const studentsQ = useQuery({
    queryKey: ['students-by-class', classId],
    queryFn: async () => (await api.get<Student[]>('/admin/students', { params: { classId } })).data,
    enabled: !!classId,
  });
  const attQ = useQuery({
    queryKey: ['attendance', classId, date],
    queryFn: async () => (await api.get<Attendance[]>('/attendance', { params: { classId, date } })).data,
    enabled: !!classId,
  });

  useEffect(() => {
    const m: Record<string, AttendanceStatus> = {};
    (studentsQ.data ?? []).forEach((s) => {
      const a = attQ.data?.find((x) => x.studentId === s.id);
      m[s.id] = a?.status ?? 'PRESENT';
    });
    setMarks(m);
  }, [studentsQ.data, attQ.data]);

  const save = useMutation({
    mutationFn: async () => {
      const entries = Object.entries(marks).map(([studentId, status]) => ({ studentId, status }));
      return (await api.put('/attendance/bulk', { classId, date, entries })).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance'] }),
  });

  const counts = useMemo(() => {
    const c: Record<string, number> = { PRESENT: 0, ABSENT: 0, LATE: 0, EXCUSED: 0 };
    Object.values(marks).forEach((s) => c[s] = (c[s] ?? 0) + 1);
    return c;
  }, [marks]);

  return (
    <Box>
      <PageHeader title="Mark Attendance" subtitle="Quickly mark daily attendance for your class." />
      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField select label="Class" value={classId} onChange={(e) => setClassId(e.target.value)} sx={{ minWidth: 200 }}>
            {(classesQ.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}-{c.section}</MenuItem>)}
          </TextField>
          <TextField type="date" label="Date" InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} sx={{ minWidth: 200 }} />
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1}>
            {STATUSES.map((s) => <Chip key={s.value} size="small" label={`${s.label}: ${counts[s.value] ?? 0}`} sx={{ bgcolor: s.color, color: '#fff' }} />)}
          </Stack>
          <Button variant="contained" startIcon={<Save />} disabled={!classId || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save attendance'}
          </Button>
        </Stack>
      </Card>

      {save.isError && <Alert severity="error" sx={{ mb: 2 }}>{extractError(save.error)}</Alert>}
      {save.isSuccess && <Alert severity="success" sx={{ mb: 2 }}>Attendance saved.</Alert>}

      {!classId ? (
        <Card sx={{ p: 6, textAlign: 'center' }}><Typography color="text.secondary">Select a class.</Typography></Card>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Roll</TableCell><TableCell>Name</TableCell><TableCell align="right">Status</TableCell></TableRow></TableHead>
            <TableBody>
              {(studentsQ.data ?? []).map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{s.rollNumber}</TableCell>
                  <TableCell>{s.firstName} {s.lastName}</TableCell>
                  <TableCell align="right">
                    <ToggleButtonGroup
                      value={marks[s.id] ?? 'PRESENT'} exclusive size="small"
                      onChange={(_, v) => v && setMarks((prev) => ({ ...prev, [s.id]: v }))}
                    >
                      {STATUSES.map((st) => (
                        <ToggleButton key={st.value} value={st.value} sx={{ minWidth: 36, '&.Mui-selected': { bgcolor: st.color, color: '#fff' } }}>
                          {st.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </TableCell>
                </TableRow>
              ))}
              {(studentsQ.data ?? []).length === 0 && (
                <TableRow><TableCell colSpan={3} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No students.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function StudentAttendanceView() {
  const { data } = useQuery({
    queryKey: ['attendance-me'],
    queryFn: async () => (await api.get<Attendance[]>('/attendance')).data,
  });
  const stats = useMemo(() => {
    const list = data ?? [];
    const counts: Record<string, number> = { PRESENT: 0, ABSENT: 0, LATE: 0, EXCUSED: 0, HOLIDAY: 0 };
    list.forEach((a) => counts[a.status] = (counts[a.status] ?? 0) + 1);
    const total = list.length;
    const present = counts.PRESENT + counts.LATE;
    const percent = total ? +(present / total * 100).toFixed(1) : 0;
    return { counts, total, present, percent };
  }, [data]);

  const monthly = useMemo(() => {
    const map: Record<string, { month: string; present: number; absent: number }> = {};
    (data ?? []).forEach((a) => {
      const m = a.date.slice(0, 7);
      const row = (map[m] ??= { month: m, present: 0, absent: 0 });
      if (a.status === 'PRESENT' || a.status === 'LATE') row.present++;
      else if (a.status === 'ABSENT') row.absent++;
    });
    return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
  }, [data]);

  const pieData = [
    { name: 'Present', value: stats.counts.PRESENT, color: '#10B981' },
    { name: 'Late', value: stats.counts.LATE, color: '#F97316' },
    { name: 'Absent', value: stats.counts.ABSENT, color: '#EF4444' },
    { name: 'Excused', value: stats.counts.EXCUSED, color: '#0EA5E9' },
  ];

  return (
    <Box>
      <PageHeader title="My Attendance" subtitle="Your attendance summary and monthly breakdown." />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">OVERALL</Typography>
              <Typography variant="h2" sx={{ fontWeight: 800, color: stats.percent >= 75 ? 'success.main' : 'error.main' }}>
                {stats.percent}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.present} of {stats.total} working days
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                <Chip size="small" color="success" label={`Present ${stats.counts.PRESENT}`} />
                <Chip size="small" color="warning" label={`Late ${stats.counts.LATE}`} />
                <Chip size="small" color="error" label={`Absent ${stats.counts.ABSENT}`} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Distribution</Typography>
              <Box sx={{ height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                      {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <Legend />
                    <RTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Monthly trend</Typography>
              <Box sx={{ height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RTooltip />
                    <Bar dataKey="present" fill="#10B981" name="Present" />
                    <Bar dataKey="absent" fill="#EF4444" name="Absent" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Recent days</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Status</TableCell><TableCell>Remarks</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(data ?? []).slice(0, 30).sort((a, b) => b.date.localeCompare(a.date)).map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.date}</TableCell>
                        <TableCell>
                          <Chip size="small" label={a.status} sx={{ bgcolor: STATUSES.find((s) => s.value === a.status)?.color ?? '#94A3B8', color: '#fff' }} />
                        </TableCell>
                        <TableCell>{a.remarks ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
